import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  isBetaFeatureEnabled,
  setBetaFeatureEnabled,
} from "@/components/beta-feature/feature-flag";
import { NotFound } from "@/components/common/not-found";
import { DSDHeader } from "@/components/dsd-view/header";
import { useAuth } from "@/contexts/auth-context";
import {
  getAutoTagVersion,
  setAutoTagVersion,
  type AutoTagVersion,
} from "@/lib/auto-tag-version";
import { isDebugEnabled, setDebugEnabled } from "@/lib/is-debug-enabled";
import { getLlmCacheEnabled, setLlmCacheEnabled } from "@/lib/llm-cache";
import { getLlmMode, setLlmMode, type LlmMode } from "@/lib/llm-mode";
import {
  isTvColorCorrectionEnabled,
  setTvColorCorrectionEnabled,
} from "@/lib/tv-color-correction";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/styles/ui/select";
import { Switch } from "@/toggle-ui-kit/control/switch";

// 플래그는 모듈 캐시 + 렌더 시점 1회 읽기 + main.tsx 초기화 시 body class 로 소비된다.
// 토글 후 전체 적용을 보장하는 가장 단순하고 확실한 방법은 새로고침이다.
const applyFlag = (write: (enabled: boolean) => void) => (next: boolean) => {
  write(next);
  window.location.reload();
};

// /settings는 admin 전용이지만 URL이 뻔해서, 시크릿 키(?key=)를 모르면 NotFound로 숨긴다.
// 진짜 인증이 아니라 발견 난이도를 올리는 obscurity 게이트(admin 체크 위에 얹는 레이어).
const LAB_ACCESS_KEY = "x7k2m9q4";

export const SettingsPage = () => {
  const { user, isLoading } = useAuth();
  const [searchParams] = useSearchParams();

  if (isLoading) return null;
  if (!user?.isAdmin) return <NotFound />;
  if (searchParams.get("key") !== LAB_ACCESS_KEY) return <NotFound />;

  return (
    <div className="min-h-screen bg-background-neutral-neutral">
      <DSDHeader />
      <div className="mx-auto max-w-3xl px-5 py-8">
        <h1 className="title-2-bold text-label-normal mb-6">실험실</h1>

        <section className="rounded-xl border border-line-neutral bg-background-base">
          <div className="divide-y divide-line-alternative">
            <SettingRow
              label="베타 기능"
              description="실험 기능을 활성화합니다."
              active={isBetaFeatureEnabled()}
              onToggle={applyFlag(setBetaFeatureEnabled)}
            />
            <SettingRow
              label="디버그 모드"
              description="상세 진단 패널과 추가 로그를 표시합니다."
              active={isDebugEnabled()}
              onToggle={applyFlag(setDebugEnabled)}
            />
            <SettingRow
              label="TV 색 보정"
              description="TV·빔프로젝터에서 밝은 회색과 어두운 색이 뭉개져 보일 때 켭니다. 일반 모니터에서는 색이 흐려 보이니 끝나면 끕니다."
              active={isTvColorCorrectionEnabled()}
              onToggle={applyFlag(setTvColorCorrectionEnabled)}
            />
            <LlmModeRow />
            <AutoTagVersionRow />
            <LlmCacheRow />
          </div>
        </section>
      </div>
    </div>
  );
};

const LLM_MODE_OPTIONS: { value: LlmMode; label: string }[] = [
  { value: "none", label: "기본" },
  { value: "mini", label: "빠름" },
  { value: "nano", label: "매우 빠름" },
];

const LlmModeRow = () => {
  const [mode, setMode] = useState<LlmMode>(getLlmMode());

  const handleChange = (next: LlmMode) => {
    setLlmMode(next);
    setMode(next);
  };

  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4">
      <div className="min-w-0">
        <p className="label-1-normal-medium text-label-normal">분석 속도</p>
        <p className="caption-1-regular text-label-alternative mt-0.5">
          분석 속도를 조정합니다. 속도가 빠를수록 분석 정확도가 낮아집니다.
        </p>
      </div>
      <Select
        value={mode}
        onValueChange={(next) => handleChange(next as LlmMode)}
      >
        <SelectTrigger className="w-28">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LLM_MODE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const AUTO_TAG_VERSION_OPTIONS: {
  value: AutoTagVersion;
  label: string;
}[] = [
  { value: "latest", label: "최신" },
  { value: "v2", label: "v2" },
  { value: "v1", label: "v1" },
];

const AutoTagVersionRow = () => {
  const [version, setVersion] = useState<AutoTagVersion>(getAutoTagVersion());

  const handleChange = (next: AutoTagVersion) => {
    setAutoTagVersion(next);
    setVersion(next);
  };

  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4">
      <div className="min-w-0">
        <p className="label-1-normal-medium text-label-normal">오토태깅 버전</p>
        <p className="caption-1-regular text-label-alternative mt-0.5">
          오토태깅 버전입니다.
        </p>
      </div>
      <Select
        value={version}
        onValueChange={(next) => handleChange(next as AutoTagVersion)}
      >
        <SelectTrigger className="w-36">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {AUTO_TAG_VERSION_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const LlmCacheRow = () => {
  const [enabled, setEnabled] = useState<boolean>(getLlmCacheEnabled());

  const handleToggle = (next: boolean) => {
    setLlmCacheEnabled(next);
    setEnabled(next);
  };

  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4">
      <div className="min-w-0">
        <p className="label-1-normal-medium text-label-normal">분석 캐시</p>
        <p className="caption-1-regular text-label-alternative mt-0.5">
          동일 입력의 분석 결과를 재사용합니다. 끄면 항상 새로 분석합니다.
        </p>
      </div>
      <Switch active={enabled} onChange={handleToggle} size={24} />
    </div>
  );
};

const SettingRow = (props: {
  label: string;
  description: string;
  active: boolean;
  onToggle: (next: boolean) => void;
}) => (
  <div className="flex items-center justify-between gap-4 px-5 py-4">
    <div className="min-w-0">
      <p className="label-1-normal-medium text-label-normal">{props.label}</p>
      <p className="caption-1-regular text-label-alternative mt-0.5">
        {props.description}
      </p>
    </div>
    <Switch active={props.active} onChange={props.onToggle} size={24} />
  </div>
);
